import React from 'react';
import { X } from 'lucide-react';
import { Scrim } from './Scrim';
import { cn } from '../lib/utils';

interface ModalProps {
  /** Whether the modal is visible */
  open: boolean;
  /** Title shown in the modal header */
  title: string;
  /** Called when the user closes the modal */
  onClose: () => void;
  /** Modal body content */
  children: React.ReactNode;
  /** Optional footer content (e.g. action buttons) */
  footer?: React.ReactNode;
  /** Extra class names on the panel */
  className?: string;
}

/**
 * Reusable modal built on the shared Scrim component.
 * Clicking outside the panel or the × button both call onClose.
 */
export function Modal({ open, title, onClose, children, footer, className}: ModalProps) {
  if (!open) return null;

  return (
    <Scrim onClick={onClose} zIndex={200}>
      <div
        className={cn(
          'rounded-2xl w-full max-w-[800px] mx-4 shadow-xl animate-in fade-in zoom-in duration-200 max-h-[90vh] overflow-y-auto',
          'bg-white text-text-primary',
          className
        )}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className={cn(
          'flex items-center justify-between px-6 pt-6 pb-4 border-b',
          'border-gray-100'
        )}>
          <h3 className="text-xl font-bold">{title}</h3>
          <button
            onClick={onClose}
            className={cn('p-1 rounded-full transition-colors', 'hover:bg-gray-100 text-text-secondary')}
          >
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-4">
          {children}
        </div>

        {/* Footer */}
        {footer && (
          <div className={cn('flex gap-3 px-6 pb-6 pt-2 border-t', 'border-gray-100')}>
            {footer}
          </div>
        )}
      </div>
    </Scrim>
  );
}
